/**
 * Custom React Hook for fetching financial metrics
 * Includes auto-refresh, loading states, and error handling
 */

import { useState, useEffect, useCallback } from 'react';
import { fetchMetrics } from '@/api/metricsApi';
import type { MetricsSummary } from '@/types/metrics';

interface UseMetricsResult {
  data: MetricsSummary | null;
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  refresh: () => Promise<void>;
}

const REFRESH_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes

/**
 * Hook for fetching and auto-refreshing financial metrics
 */
export function useMetrics(): UseMetricsResult {
  const [data, setData] = useState<MetricsSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const metrics = await fetchMetrics();
      setData(metrics);
      setLastUpdated(new Date());
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch metrics';
      setError(errorMessage);
      console.error('Error fetching metrics:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch on mount
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Auto-refresh every 15 minutes
  useEffect(() => {
    const intervalId = setInterval(() => {
      refresh();
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [refresh]);

  return {
    data,
    loading,
    error,
    lastUpdated,
    refresh,
  };
}
